import { API_TYPES } from "@src/appV2/api/constants";
import { post } from "@src/appV2/api/parsedApi";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { GET_FACILITY_DAY_SHIFTS_URL } from "./queryKeys";

const workerLeftEarlyRequestBodySchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.workerLeftEarlyRequest),
    id: z.string(),
    attributes: z.object({
      shiftId: z.string(),
      replacementRequested: z.boolean(),
      leftWithPermission: z.boolean(),
      comment: z.string(),
    }),
  }),
});

const workerLeftEarlyResponseSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.workerLeftEarlyRequest),
    id: z.string(),
    attributes: z.object({
      shiftId: z.string(),
      replacementRequested: z.boolean(),
      leftWithPermission: z.boolean(),
      comment: z.string(),
    }),
  }),
});

export interface ReportWorkerLeftEarlyParams {
  shiftId: string;
  replacementRequested: boolean;
  leftWithPermission: boolean;
  comment: string;
}

/**
 * Reports that a worker left their shift early.
 * Uses the JSON:API format expected by the /worker-left-early-requests endpoint.
 * Invalidates day shifts cache on success.
 */
export function useReportWorkerLeftEarly() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ReportWorkerLeftEarlyParams) => {
      const response = await post({
        url: "/worker-left-early-requests",
        data: {
          data: {
            type: API_TYPES.workerLeftEarlyRequest,
            id: params.shiftId,
            attributes: {
              shiftId: params.shiftId,
              replacementRequested: params.replacementRequested,
              leftWithPermission: params.leftWithPermission,
              comment: params.comment,
            },
          },
        },
        bodySchema: workerLeftEarlyRequestBodySchema,
        responseSchema: workerLeftEarlyResponseSchema,
      });

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [GET_FACILITY_DAY_SHIFTS_URL],
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.REPORT_WORKER_LEFT_EARLY_FAILURE,
    },
  });
}
